import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import './GameCreator.css';

const GameCreator = () => {
  const navigate = useNavigate();
  const [gameData, setGameData] = useState({
    title: '',
    description: '',
    subject: '',
    gameType: 'quiz',
    difficulty: 'medium',
    examType: 'general',
    topics: [],
    config: {
      timeLimit: 30,
      allowHints: true,
      shuffleQuestions: true,
      shuffleOptions: true,
      passingScore: 70,
      maxAttempts: 3,
      showCorrectAnswers: true,
      allowReview: true
    },
    questions: [],
    tags: []
  });
  
  const [currentQuestion, setCurrentQuestion] = useState({
    question: '',
    type: 'multiple_choice',
    options: [{ id: 'a', text: '' }, { id: 'b', text: '' }],
    correctAnswer: '',
    explanation: '',
    hint: '',
    points: 10,
    difficulty: 'medium'
  });
  
  const [editingIndex, setEditingIndex] = useState(-1);
  const [activeTab, setActiveTab] = useState('basic');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const subjects = [
    'Mathematics', 'Science', 'English', 'History', 'Geography',
    'Physics', 'Chemistry', 'Biology', 'Computer Science', 'Art'
  ];

  const examTypes = [
    'general', 'midterm', 'final', 'quiz', 'assignment', 'practice'
  ];

  const difficulties = [
    { value: 'easy', label: 'Easy', color: '#4CAF50' },
    { value: 'medium', label: 'Medium', color: '#FF9800' },
    { value: 'hard', label: 'Hard', color: '#f44336' }
  ];

  const questionTypes = [
    { value: 'multiple_choice', label: 'Multiple Choice' },
    { value: 'true_false', label: 'True/False' },
    { value: 'fill_blank', label: 'Fill in the Blank' }
  ];

  useEffect(() => {
    // Clear messages after 5 seconds
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  const handleGameDataChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setGameData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setGameData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleQuestionChange = (field, value) => {
    setCurrentQuestion(prev => ({ ...prev, [field]: value }));
  };

  const handleOptionChange = (index, value) => {
    setCurrentQuestion(prev => ({
      ...prev,
      options: prev.options.map((opt, i) => 
        i === index ? { ...opt, text: value } : opt
      )
    }));
  };

  const addOption = () => {
    const nextLetter = String.fromCharCode(97 + currentQuestion.options.length);
    setCurrentQuestion(prev => ({
      ...prev,
      options: [...prev.options, { id: nextLetter, text: '' }]
    }));
  };

  const removeOption = (index) => {
    if (currentQuestion.options.length > 2) {
      setCurrentQuestion(prev => ({
        ...prev,
        options: prev.options.filter((_, i) => i !== index)
      }));
    }
  };

  const validateQuestion = () => {
    if (!currentQuestion.question.trim()) {
      setError('Question text is required');
      return false;
    }

    if (currentQuestion.type === 'multiple_choice') {
      if (currentQuestion.options.some(opt => !opt.text.trim())) {
        setError('All options must have text');
        return false;
      }
      if (!currentQuestion.correctAnswer) {
        setError('Please select the correct answer');
        return false;
      }
    }

    if (currentQuestion.type === 'true_false') {
      if (!currentQuestion.correctAnswer) {
        setError('Please select the correct answer (True or False)');
        return false;
      }
    }

    if (currentQuestion.type === 'fill_blank') {
      if (!currentQuestion.correctAnswer.trim()) {
        setError('Correct answer is required for fill-in-the-blank questions');
        return false;
      }
    }

    return true;
  };

  const addQuestion = () => {
    if (!validateQuestion()) return;

    const questionToAdd = {
      ...currentQuestion,
      id: Date.now().toString(),
      options: currentQuestion.type === 'multiple_choice' ? currentQuestion.options : undefined
    };

    if (editingIndex >= 0) {
      setGameData(prev => ({
        ...prev,
        questions: prev.questions.map((q, i) => 
          i === editingIndex ? questionToAdd : q
        )
      }));
      setEditingIndex(-1);
      setSuccess('Question updated successfully');
    } else {
      setGameData(prev => ({
        ...prev,
        questions: [...prev.questions, questionToAdd]
      }));
      setSuccess('Question added successfully');
    }

    resetCurrentQuestion();
  };

  const editQuestion = (index) => {
    const question = gameData.questions[index];
    setCurrentQuestion({
      ...question,
      options: question.options || [{ id: 'a', text: '' }, { id: 'b', text: '' }]
    });
    setEditingIndex(index);
    setActiveTab('questions');
  };

  const deleteQuestion = (index) => {
    setGameData(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }));
    setSuccess('Question deleted successfully');
  };

  const resetCurrentQuestion = () => {
    setCurrentQuestion({
      question: '',
      type: 'multiple_choice',
      options: [{ id: 'a', text: '' }, { id: 'b', text: '' }],
      correctAnswer: '',
      explanation: '',
      hint: '',
      points: 10,
      difficulty: 'medium'
    });
  };

  const validateGame = () => {
    if (!gameData.title.trim()) {
      setError('Game title is required');
      return false;
    }
    if (!gameData.description.trim()) {
      setError('Game description is required');
      return false;
    }
    if (!gameData.subject) {
      setError('Please select a subject');
      return false;
    }
    if (gameData.questions.length === 0) {
      setError('At least one question is required');
      return false;
    }
    return true;
  };

  const saveGame = async () => {
    if (!validateGame()) return;

    try {
      setLoading(true);
      setError(null);

      const response = await api.post('/games/custom', gameData);
      
      if (response.data.success) {
        setSuccess('Game created successfully!');
        setTimeout(() => {
          navigate('/games');
        }, 2000);
      }
    } catch (err) {
      console.error('Error creating game:', err);
      setError(err.response?.data?.message || 'Failed to create game');
    } finally {
      setLoading(false);
    }
  };

  const addTag = (tag) => {
    if (tag && !gameData.tags.includes(tag)) {
      setGameData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
    }
  };

  const removeTag = (tagToRemove) => {
    setGameData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const addTopic = (topic) => {
    if (topic && !gameData.topics.includes(topic)) {
      setGameData(prev => ({
        ...prev,
        topics: [...prev.topics, topic]
      }));
    }
  };

  const removeTopic = (topicToRemove) => {
    setGameData(prev => ({
      ...prev,
      topics: prev.topics.filter(topic => topic !== topicToRemove)
    }));
  };

  return (
    <div className="game-creator">
      <div className="creator-header">
        <h1>Create Educational Game</h1>
        <p>Design interactive games to engage your students</p>
      </div>

      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠️</span>
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <span className="alert-icon">✅</span>
          {success}
        </div>
      )}

      <div className="creator-tabs">
        <button 
          className={`tab-button ${activeTab === 'basic' ? 'active' : ''}`}
          onClick={() => setActiveTab('basic')}
        >
          Basic Info
        </button>
        <button 
          className={`tab-button ${activeTab === 'config' ? 'active' : ''}`}
          onClick={() => setActiveTab('config')}
        >
          Configuration
        </button>
        <button 
          className={`tab-button ${activeTab === 'questions' ? 'active' : ''}`}
          onClick={() => setActiveTab('questions')}
        >
          Questions ({gameData.questions.length})
        </button>
        <button 
          className={`tab-button ${activeTab === 'preview' ? 'active' : ''}`}
          onClick={() => setActiveTab('preview')}
        >
          Preview
        </button>
      </div>

      <div className="creator-content">
        {activeTab === 'basic' && (
          <div className="tab-content">
            <div className="form-group">
              <label>Game Title *</label>
              <input
                type="text"
                value={gameData.title}
                onChange={(e) => handleGameDataChange('title', e.target.value)}
                placeholder="Enter game title"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>Description *</label>
              <textarea
                value={gameData.description}
                onChange={(e) => handleGameDataChange('description', e.target.value)}
                placeholder="Describe what this game is about"
                className="form-textarea"
                rows={4}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Subject *</label>
                <select
                  value={gameData.subject}
                  onChange={(e) => handleGameDataChange('subject', e.target.value)}
                  className="form-select"
                >
                  <option value="">Select Subject</option>
                  {subjects.map(subject => (
                    <option key={subject} value={subject}>{subject}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Difficulty</label>
                <select
                  value={gameData.difficulty}
                  onChange={(e) => handleGameDataChange('difficulty', e.target.value)}
                  className="form-select"
                >
                  {difficulties.map(diff => (
                    <option key={diff.value} value={diff.value}>{diff.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Exam Type</label>
                <select
                  value={gameData.examType}
                  onChange={(e) => handleGameDataChange('examType', e.target.value)}
                  className="form-select"
                >
                  {examTypes.map(type => (
                    <option key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Topics</label>
              <div className="tag-input-container">
                <input
                  type="text"
                  placeholder="Add topic and press Enter"
                  className="tag-input"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addTopic(e.target.value.trim());
                      e.target.value = '';
                    }
                  }}
                />
                <div className="tags-display">
                  {gameData.topics.map((topic, index) => (
                    <span key={index} className="tag">
                      {topic}
                      <button onClick={() => removeTopic(topic)} className="tag-remove">
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>Tags</label>
              <div className="tag-input-container">
                <input
                  type="text"
                  placeholder="Add tag and press Enter"
                  className="tag-input"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addTag(e.target.value.trim());
                      e.target.value = '';
                    }
                  }}
                />
                <div className="tags-display">
                  {gameData.tags.map((tag, index) => (
                    <span key={index} className="tag">
                      {tag}
                      <button onClick={() => removeTag(tag)} className="tag-remove">
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'config' && (
          <div className="tab-content">
            <div className="config-section">
              <h3>Game Settings</h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Time Limit (seconds)</label>
                  <input
                    type="number"
                    value={gameData.config.timeLimit}
                    onChange={(e) => handleGameDataChange('config.timeLimit', parseInt(e.target.value))}
                    min="10"
                    max="300"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>Passing Score (%)</label>
                  <input
                    type="number"
                    value={gameData.config.passingScore}
                    onChange={(e) => handleGameDataChange('config.passingScore', parseInt(e.target.value))}
                    min="0"
                    max="100"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>Max Attempts</label>
                  <input
                    type="number"
                    value={gameData.config.maxAttempts}
                    onChange={(e) => handleGameDataChange('config.maxAttempts', parseInt(e.target.value))}
                    min="1"
                    max="10"
                    className="form-input"
                  />
                </div>
              </div>

              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={gameData.config.allowHints}
                    onChange={(e) => handleGameDataChange('config.allowHints', e.target.checked)}
                  />
                  Allow Hints
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={gameData.config.shuffleQuestions}
                    onChange={(e) => handleGameDataChange('config.shuffleQuestions', e.target.checked)}
                  />
                  Shuffle Questions
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={gameData.config.shuffleOptions}
                    onChange={(e) => handleGameDataChange('config.shuffleOptions', e.target.checked)}
                  />
                  Shuffle Answer Options
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={gameData.config.showCorrectAnswers}
                    onChange={(e) => handleGameDataChange('config.showCorrectAnswers', e.target.checked)}
                  />
                  Show Correct Answers After Each Question
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={gameData.config.allowReview}
                    onChange={(e) => handleGameDataChange('config.allowReview', e.target.checked)}
                  />
                  Allow Review After Completion
                </label>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'questions' && (
          <div className="tab-content">
            <div className="question-editor">
              <h3>{editingIndex >= 0 ? 'Edit Question' : 'Add New Question'}</h3>
              
              <div className="form-group">
                <label>Question Type</label>
                <select
                  value={currentQuestion.type}
                  onChange={(e) => handleQuestionChange('type', e.target.value)}
                  className="form-select"
                >
                  {questionTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Question Text *</label>
                <textarea
                  value={currentQuestion.question}
                  onChange={(e) => handleQuestionChange('question', e.target.value)}
                  placeholder="Enter your question"
                  className="form-textarea"
                  rows={3}
                />
              </div>

              {currentQuestion.type === 'multiple_choice' && (
                <div className="form-group">
                  <label>Answer Options *</label>
                  <div className="options-editor">
                    {currentQuestion.options.map((option, index) => (
                      <div key={index} className="option-row">
                        <span className="option-letter">
                          {String.fromCharCode(65 + index)}
                        </span>
                        <input
                          type="text"
                          value={option.text}
                          onChange={(e) => handleOptionChange(index, e.target.value)}
                          placeholder={`Option ${String.fromCharCode(65 + index)}`}
                          className="option-input"
                        />
                        <input
                          type="radio"
                          name="correctAnswer"
                          value={option.id}
                          checked={currentQuestion.correctAnswer === option.id}
                          onChange={(e) => handleQuestionChange('correctAnswer', e.target.value)}
                          className="correct-radio"
                        />
                        {currentQuestion.options.length > 2 && (
                          <button
                            onClick={() => removeOption(index)}
                            className="remove-option"
                            type="button"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ))}
                    {currentQuestion.options.length < 6 && (
                      <button onClick={addOption} className="add-option" type="button">
                        + Add Option
                      </button>
                    )}
                  </div>
                </div>
              )}

              {currentQuestion.type === 'true_false' && (
                <div className="form-group">
                  <label>Correct Answer *</label>
                  <div className="true-false-options">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="tfAnswer"
                        value="true"
                        checked={currentQuestion.correctAnswer === 'true'}
                        onChange={(e) => handleQuestionChange('correctAnswer', e.target.value)}
                      />
                      True
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="tfAnswer"
                        value="false"
                        checked={currentQuestion.correctAnswer === 'false'}
                        onChange={(e) => handleQuestionChange('correctAnswer', e.target.value)}
                      />
                      False
                    </label>
                  </div>
                </div>
              )}

              {currentQuestion.type === 'fill_blank' && (
                <div className="form-group">
                  <label>Correct Answer *</label>
                  <input
                    type="text"
                    value={currentQuestion.correctAnswer}
                    onChange={(e) => handleQuestionChange('correctAnswer', e.target.value)}
                    placeholder="Enter the correct answer"
                    className="form-input"
                  />
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Points</label>
                  <input
                    type="number"
                    value={currentQuestion.points}
                    onChange={(e) => handleQuestionChange('points', parseInt(e.target.value))}
                    min="1"
                    max="100"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>Difficulty</label>
                  <select
                    value={currentQuestion.difficulty}
                    onChange={(e) => handleQuestionChange('difficulty', e.target.value)}
                    className="form-select"
                  >
                    {difficulties.map(diff => (
                      <option key={diff.value} value={diff.value}>{diff.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Hint (Optional)</label>
                <input
                  type="text"
                  value={currentQuestion.hint}
                  onChange={(e) => handleQuestionChange('hint', e.target.value)}
                  placeholder="Provide a helpful hint"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Explanation (Optional)</label>
                <textarea
                  value={currentQuestion.explanation}
                  onChange={(e) => handleQuestionChange('explanation', e.target.value)}
                  placeholder="Explain the correct answer"
                  className="form-textarea"
                  rows={3}
                />
              </div>

              <div className="question-actions">
                <button onClick={addQuestion} className="add-question-btn">
                  {editingIndex >= 0 ? 'Update Question' : 'Add Question'}
                </button>
                {editingIndex >= 0 && (
                  <button 
                    onClick={() => {
                      setEditingIndex(-1);
                      resetCurrentQuestion();
                    }} 
                    className="cancel-edit-btn"
                  >
                    Cancel Edit
                  </button>
                )}
              </div>
            </div>

            {gameData.questions.length > 0 && (
              <div className="questions-list">
                <h3>Questions ({gameData.questions.length})</h3>
                {gameData.questions.map((question, index) => (
                  <div key={question.id} className="question-item">
                    <div className="question-header">
                      <span className="question-number">Q{index + 1}</span>
                      <span className="question-type">{question.type.replace('_', ' ')}</span>
                      <span className="question-points">{question.points} pts</span>
                    </div>
                    <div className="question-text">{question.question?.text || question.question?.question || (typeof question.question === 'string' ? question.question : 'Question text not available')}</div>
                    <div className="question-actions">
                      <button onClick={() => editQuestion(index)} className="edit-btn">
                        Edit
                      </button>
                      <button onClick={() => deleteQuestion(index)} className="delete-btn">
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'preview' && (
          <div className="tab-content">
            <div className="game-preview">
              <div className="preview-header">
                <h2>{gameData.title || 'Untitled Game'}</h2>
                <p>{gameData.description || 'No description provided'}</p>
                <div className="preview-meta">
                  <span className="meta-item">Subject: {gameData.subject || 'Not selected'}</span>
                  <span className="meta-item">Difficulty: {gameData.difficulty}</span>
                  <span className="meta-item">Questions: {gameData.questions.length}</span>
                </div>
              </div>

              <div className="preview-config">
                <h3>Game Configuration</h3>
                <div className="config-grid">
                  <div className="config-item">
                    <span className="config-label">Time Limit:</span>
                    <span className="config-value">{gameData.config.timeLimit}s</span>
                  </div>
                  <div className="config-item">
                    <span className="config-label">Passing Score:</span>
                    <span className="config-value">{gameData.config.passingScore}%</span>
                  </div>
                  <div className="config-item">
                    <span className="config-label">Max Attempts:</span>
                    <span className="config-value">{gameData.config.maxAttempts}</span>
                  </div>
                  <div className="config-item">
                    <span className="config-label">Hints Allowed:</span>
                    <span className="config-value">{gameData.config.allowHints ? 'Yes' : 'No'}</span>
                  </div>
                </div>
              </div>

              {gameData.questions.length > 0 && (
                <div className="preview-questions">
                  <h3>Questions Preview</h3>
                  {gameData.questions.slice(0, 3).map((question, index) => (
                    <div key={question.id} className="preview-question">
                      <div className="preview-question-header">
                        <span className="preview-question-number">Question {index + 1}</span>
                        <span className="preview-question-points">{question.points} points</span>
                      </div>
                      <div className="preview-question-text">{question.question?.text || question.question?.question || (typeof question.question === 'string' ? question.question : 'Question text not available')}</div>
                      {question.type === 'multiple_choice' && (
                        <div className="preview-options">
                          {question.options.map((option, optIndex) => (
                            <div key={option.id} className="preview-option">
                              {String.fromCharCode(65 + optIndex)}. {option.text}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  {gameData.questions.length > 3 && (
                    <div className="preview-more">
                      ... and {gameData.questions.length - 3} more questions
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="creator-footer">
        <button onClick={() => navigate('/games')} className="cancel-btn">
          Cancel
        </button>
        <button 
          onClick={saveGame} 
          disabled={loading || gameData.questions.length === 0}
          className="save-btn"
        >
          {loading ? 'Creating...' : 'Create Game'}
        </button>
      </div>
    </div>
  );
};

export default GameCreator;